import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { 
  LayoutDashboard, 
  Calendar,
  Image,
  BarChart2,
  Building2,
  Share2,
  Users2,
  Target,
  Settings,
  Sparkles,
  Menu,
  X,
  CreditCard,
  Plus,
  LogOut,
  Flame,
  FileText,
  Plug,
  Link as LinkIcon,
} from 'lucide-react';
import { CreatePostModal } from '../posts/CreatePostModal';
import { auth } from '../../config/firebase';

const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const { user, clearStore } = useAuthStore();

  const menuItems = [
    {
      category: t('sidebar.categories.summary'),
      items: [
        { icon: Flame, label: 'Onboarding', path: '/onboarding' },
        { icon: LayoutDashboard, label: t('common.dashboard'), path: '/dashboard' },
      ]
    },
    {
      category: t('sidebar.categories.content'),
      items: [
        { icon: Calendar, label: t('calendar.title'), path: '/calendar' },
        { icon: FileText, label: t('contentHub.title'), path: '/my-content-hub' }
      ]
    },
    {
      category: t('sidebar.categories.aiStudio'),
      items: [
        { icon: Sparkles, label: t('contentCreation.title'), path: '/content-creation' },
        { icon: Building2, label: t('brands.title'), path: '/brand-strategy-lab' },
        { icon: Target, label: t('personas.title'), path: '/ai-personas-builder' },
        { icon: Share2, label: t('accounts.title'), path: '/social-media-hub' },
        { icon: LinkIcon, label: t('sidebar.aiLinksBuilder'), path: '/ai-links-builder' },
        { icon: BarChart2, label: t('analytics.title'), path: '/analytics' }
      ]
    },
    {
      category: t('sidebar.categories.management'),
      items: [
        { icon: Image, label: t('sidebar.media'), path: '/media' },
        { icon: Users2, label: t('sidebar.team'), path: '/team' },
        { icon: Plug, label: t('sidebar.integrations'), path: '/integrations' },
      ]
    }
  ];

  const bottomMenuItems = [
    { icon: CreditCard, label: t('common.billing'), path: '/billing' },
    { icon: Settings, label: t('common.settings'), path: '/settings' },
  ];

  const handleCreateWithAI = () => {
    console.log('Creating post with AI');
    setIsCreateModalOpen(false);
    navigate('/content-creation');
  };

  const handleCreateManually = () => {
    setIsCreateModalOpen(false);
    navigate('/posts/new');
  };

  const handleSignOut = async () => {
    // Clear all React Query cache
    queryClient.clear();
    // Clear auth store
    clearStore();
    // Sign out from Firebase
    await auth.signOut();
    // Navigate to login page
    navigate('/auth/login');
  };

  // Format the display name before returning JSX
  let displayName = "User"; // Default fallback
  if (user?.firstName || user?.lastName) {
    // Construct from first/last name if available, capitalize each part
    const first = user.firstName ? user.firstName.charAt(0).toUpperCase() + user.firstName.slice(1).toLowerCase() : '';
    const last = user.lastName ? user.lastName.charAt(0).toUpperCase() + user.lastName.slice(1).toLowerCase() : '';
    displayName = `${first} ${last}`.trim(); // Combine and trim potential extra space if one is missing
  } else if (user?.name) {
    // Fallback to capitalizing the existing full name field
    displayName = user.name
      .split(' ')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  } else if (user?.email) {
    // Final fallback to email prefix
    displayName = user.email.split('@')[0];
  }

  return (
    <>
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-[60] px-4 flex items-center">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6 text-gray-600" />
          ) : (
            <Menu className="w-6 h-6 text-gray-600" />
          )}
        </button>
        <img 
          src="/assets/Socialness-Logo/Socialness-Icon-1024x1024.png" 
          alt="Socialness" 
          className="h-10 w-10 ml-4 object-contain"
        />
      </div>

      {/* Sidebar */}
      <aside 
        className={`fixed top-16 lg:top-0 bottom-0 left-0 z-[51] bg-white border-r border-gray-200 transform transition-all duration-200 ease-in-out lg:translate-x-0 flex flex-col overflow-hidden shadow-none hover:shadow-xl ${
          isMobileMenuOpen 
            ? 'translate-x-0 w-64' // Keep mobile expanded width as is for now
            : '-translate-x-full lg:translate-x-0 lg:w-20 lg:hover:w-72' // Increased lg:w-16 to lg:w-20, lg:hover:w-64 to lg:hover:w-72
        }`}
        onMouseEnter={() => !isMobileMenuOpen && setIsExpanded(true)}
        onMouseLeave={() => !isMobileMenuOpen && setIsExpanded(false)}
      >
        {/* Logo */}
        <div className="hidden lg:flex flex-shrink-0 h-16 items-center justify-center border-b border-gray-200">
          <Link to="/" className="relative flex items-center justify-center">
            <img 
              src="/assets/Socialness-Logo/Socialness-Icon-1024x1024.png" 
              alt="Socialness" 
              className="w-10 h-10 object-contain"
            />
            <span 
              className={`font-title font-extrabold text-xl text-gray-900 transition-all duration-200 ${
                isMobileMenuOpen || isExpanded 
                  ? 'relative ml-2 opacity-100' 
                  : 'absolute left-full opacity-0 invisible'
              }`}
            >
              Socialness
            </span>
          </Link>
        </div>

        {/* Create Content Button */}
        <div className="p-1.5 border-b border-gray-200">
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className={`w-full flex items-center justify-center space-x-2 p-1 bg-[#7136ed] text-white rounded-lg hover:bg-[#6429e3] transition-colors ${
              isMobileMenuOpen || isExpanded ? 'px-4' : 'px-2' // Use px-4 when expanded/mobile
            }`}
          >
            <Plus className="w-5 h-5 flex-shrink-0" />
            <span className={`${isMobileMenuOpen || isExpanded ? 'inline' : 'hidden'}`}>
              {t('sidebar.createContent')}
            </span>
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto">
          {menuItems.map((section) => (
            <div key={section.category}>
              <h3 className={`h-5 flex items-center px-4 text-[10px] font-semibold text-gray-400 uppercase tracking-wider ${
                !isMobileMenuOpen && !isExpanded ? 'lg:opacity-0' : 'opacity-100'
              } transition-opacity duration-200 mt-1.5`}> 
                {section.category}
              </h3>
              <div>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`w-full flex items-center h-7 relative group ${
                        isActive
                          ? 'text-primary-600'
                          : 'text-gray-700 hover:text-primary-600'
                      }`}
                      title={item.label}
                    >
                      {isActive && (
                        <div className="absolute inset-0 bg-primary-50" />
                      )}
                      
                      <div className={`flex items-center w-full ${
                        isMobileMenuOpen || isExpanded ? 'px-4' : 'justify-center'
                      }`}>
                        <div className="relative flex items-center justify-center w-5 flex-shrink-0">
                          <Icon className="w-4 h-4" />
                        </div>
                        
                        <span 
                          className={`transition-all duration-200 text-sm whitespace-nowrap ${
                            isMobileMenuOpen || isExpanded 
                              ? 'relative ml-3 opacity-100' 
                              : 'absolute left-full opacity-0 invisible'
                          }`}
                        >
                          {item.label}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Fixed Bottom Account Management Links */}
        <div className="flex-shrink-0 py-2 border-t border-gray-200">
          <h3 className={`h-5 flex items-center px-4 text-[10px] font-semibold text-gray-400 uppercase tracking-wider ${
            !isMobileMenuOpen && !isExpanded ? 'lg:opacity-0' : 'opacity-100'
          } transition-opacity duration-200 mt-1.5`}>
            {t('sidebar.categories.admin')}
          </h3>
          {bottomMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`w-full flex items-center h-7 relative group ${
                  isActive
                    ? 'text-primary-600'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
                title={item.label}
              >
                {isActive && (
                  <div className="absolute inset-0 bg-primary-50" />
                )}
                <div className={`flex items-center w-full ${
                  isMobileMenuOpen || isExpanded ? 'px-4' : 'justify-center'
                }`}> 
                  <div className="relative flex items-center justify-center w-5 flex-shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <span 
                    className={`transition-all duration-200 text-sm whitespace-nowrap ${
                      isMobileMenuOpen || isExpanded 
                        ? 'relative ml-3 opacity-100' 
                        : 'absolute left-full opacity-0 invisible'
                    }`}
                  >
                    {item.label}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        {/* User Profile */}
        <div className="flex-shrink-0 p-2 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
              {user?.photoURL ? (
                <img 
                  src={user.photoURL} 
                  alt={user?.name || 'User'} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                  {user?.name?.slice(0, 2) || user?.email?.slice(0, 2).toUpperCase() || 'U'}
                </div>
              )}
            </div>
            <div className={`min-w-0 flex-1 transition-opacity duration-200 ${
              isMobileMenuOpen || isExpanded ? 'opacity-100' : 'lg:opacity-0 w-0'
            }`}>
              <p className="text-sm font-medium text-gray-900 truncate">
                {displayName} {/* Use the correctly formatted displayName */}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email}
              </p>
              <p className="text-xs flex items-center">
                <button
                  onClick={handleSignOut}
                  className="inline-flex items-center text-red-600 hover:text-red-700"
                >
                  <LogOut className="w-3 h-3 mr-1" />
                  <span className="text-xs">{t('common.logout')}</span>
                </button>
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 lg:hidden z-[50]"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateWithAI={handleCreateWithAI}
        onCreateManually={handleCreateManually}
      />
    </>
  );
};

export default Sidebar;